import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  ArrowLeft, Download, FileText, Users, Target, 
  TrendingUp, DollarSign, Palette, CheckCircle, 
  XCircle, Loader2, Eye, Sparkles, Mail, Send,
  BarChart3, Rocket, ClipboardList
} from "lucide-react";

export const MyOverviewPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipients: "", subject: "My Business Success Summary", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchPreviewData();
  }, []);

  const fetchPreviewData = async () => {
    try {
      const response = await authFetch(`${API}/client-success-summary/preview`, {  });
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
      }
    } catch (_error) {
      console.error("Error fetching preview:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailReport = async () => {
    const recipients = emailForm.recipients.split(',').map(e => e.trim()).filter(e => e);
    
    if (recipients.length === 0) {
      toast.error("Please enter at least one email address");
      return;
    }

    setSending(true);
    try {
      const response = await authFetch(`${API}/client-success-summary/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          recipients,
          subject: emailForm.subject,
          message: emailForm.message
        })
      });

      if (response.ok) {
        toast.success(`Report sent to ${recipients.length} recipient(s)!`);
        setShowEmailDialog(false);
        setEmailForm({ recipients: "", subject: "My Business Success Summary", message: "" });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (_error) {
      toast.error("An error occurred while sending");
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await authFetch(`${API}/client-success-summary/pdf`, {  });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `client_success_summary_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("PDF downloaded successfully!");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (_error) {
      console.error("Error downloading PDF:", _error);
      toast.error("An error occurred while generating the PDF");
    } finally {
      setDownloading(false);
    }
  };

  const sections = [
    {
      id: "ideal_client",
      title: "Ideal Client Profile",
      description: "Your target customer demographics, pain points, and desired outcomes",
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      link: "/ideal-client"
    },
    {
      id: "vision_goals",
      title: "Vision & Goals",
      description: "Your 1-year vision, goals, and financial targets",
      icon: Target,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      link: "/vision-goals"
    },
    {
      id: "action_plans",
      title: "Action Plans",
      description: "Operations, process, and marketing action plans with tasks",
      icon: FileText,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      link: "/action-plans"
    },
    {
      id: "sales_data",
      title: "Sales Performance",
      description: "Quarterly sales tracking, revenue, and profit data",
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-50",
      link: "/sales-tracking"
    },
    {
      id: "brand_guidelines",
      title: "Brand Guidelines",
      description: "Brand identity, values, voice, and visual guidelines",
      icon: Palette,
      color: "text-rose-600",
      bgColor: "bg-rose-50",
      link: "/brand-guidelines"
    },
    {
      id: "scaling_tools",
      title: "AI Scaling Tools",
      description: "Health Snapshot, KPIs, Tech Stack, Marketing Strategy, Org Map & more",
      icon: BarChart3,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      link: "/scaling",
      isNew: true
    },
    {
      id: "engagements",
      title: "Growth Engagements",
      description: "10 progressive business modules unlocking over your 6-month program",
      icon: Rocket,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      link: "/engagements",
      isNew: true
    },
    {
      id: "sops",
      title: "SOP Generator",
      description: "Standard Operating Procedures to systemize your key business processes",
      icon: ClipboardList,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      link: "/sop-generator",
      isNew: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="my-overview-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">My Overview</h1>
                <p className="text-sm text-neutral-500">Generate your Client Success Summary</p>
              </div>
            </div>
            <Button 
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero Card */}
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white mb-8">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-center md:text-left mb-6 md:mb-0">
                <div className="flex items-center justify-center md:justify-start space-x-2 mb-3">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Client Success Summary</span>
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                  Hello, {previewData?.client_name || "there"}!
                </h2>
                <p className="text-neutral-600 max-w-md">
                  Generate a professional PDF overview of your business strategy, goals, 
                  and progress to share or print.
                </p>
              </div>
              <div className="text-center">
                <div className="w-28 h-28 rounded-full bg-white border-4 border-emerald-200 flex items-center justify-center mb-2">
                  <span className="text-3xl font-bold text-emerald-700">{previewData?.completeness || 0}%</span>
                </div>
                <p className="text-sm text-neutral-600">Profile Complete</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections Grid */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">What's Included in Your PDF</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sections.map((section) => {
              const isComplete = previewData?.sections?.[section.id];
              const Icon = section.icon;
              
              return (
                <Card 
                  key={section.id}
                  className={`border-neutral-200 transition-all hover:shadow-md cursor-pointer ${
                    isComplete ? "bg-white" : "bg-neutral-50"
                  }`}
                  onClick={() => navigate(section.link)}
                  data-testid={`section-${section.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg ${section.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${section.color}`} />
                      </div>
                      <div className="flex items-center gap-1.5">
                        {section.isNew && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">New</Badge>
                        )}
                        {isComplete ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Ready
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-neutral-500 border-neutral-300">
                            <XCircle className="w-3 h-3 mr-1" />
                            Incomplete
                          </Badge>
                        )}
                      </div>
                    </div>
                    <h4 className="font-semibold text-neutral-900 mb-1">{section.title}</h4>
                    <p className="text-sm text-neutral-500 line-clamp-2">{section.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Data Summary */}
        {previewData && (
          <Card className="border-neutral-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center">
                <Eye className="w-5 h-5 mr-2 text-neutral-600" />
                Data Preview
              </CardTitle>
              <CardDescription>Quick summary of what will be in your PDF</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {previewData.data.ideal_client ? "✓" : "—"}
                  </p>
                  <p className="text-xs text-blue-600">Ideal Client</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {previewData.data.action_plans_count}
                  </p>
                  <p className="text-xs text-purple-600">Action Plans</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">
                    ${(previewData.data.total_revenue / 1000).toFixed(0)}k
                  </p>
                  <p className="text-xs text-green-600">Total Revenue</p>
                </div>
                <div className="p-4 bg-rose-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-rose-700">
                    {previewData.data.brand_guidelines ? "✓" : "—"}
                  </p>
                  <p className="text-xs text-rose-600">Brand Guidelines</p>
                </div>
              </div>
              
              {previewData.completeness < 100 && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Tip:</strong> Complete all sections for a comprehensive PDF. 
                    Click on any incomplete section above to add your data.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Download Section */}
        <div className="mt-8 text-center space-y-4">
          <div className="flex justify-center space-x-4">
            <Button 
              size="lg"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-emerald-600 hover:bg-emerald-700 h-14 px-8"
              data-testid="download-pdf-btn-bottom"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowEmailDialog(true)}
              className="h-14 px-8 border-blue-300 text-blue-600 hover:bg-blue-50"
              data-testid="email-report-btn"
            >
              <Mail className="w-5 h-5 mr-2" />
              Email Report
            </Button>
          </div>
          <p className="text-sm text-neutral-500">
            PDF includes all your business data formatted for printing or sharing
          </p>
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2 text-blue-600" />
              Email Success Summary
            </DialogTitle>
            <DialogDescription>
              Send your Client Success Summary PDF to yourself or others
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipients">Recipients (comma-separated)</Label>
              <Input
                id="recipients"
                placeholder="email@example.com, another@example.com"
                value={emailForm.recipients}
                onChange={(e) => setEmailForm(prev => ({ ...prev, recipients: e.target.value }))}
                data-testid="email-recipients-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Personal Message (optional)</Label>
              <Textarea
                id="message"
                placeholder="Add a personal note to accompany the report..."
                value={emailForm.message}
                onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                className="min-h-20"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEmailReport} 
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyOverviewPage;
